/* eslint-disable @nx/enforce-module-boundaries */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { delay, filter, first, map, switchMap, take, tap, timer } from 'rxjs';

import { environment } from '@ninety/mobile/platform/environments';
import { DatadogLogger, SEGMENT_EVENT_METADATA, SegmentContactTraits } from '@ninety/mobile/platform/services';

import { appActions } from '../../../app/app.actions';
import { CompanyUserSelectors } from '../../../app-entities/company-users/company-users-state.selectors';
import { capacitorAppActions } from '../../../capacitor/app/capacitor-app.actions';
import { SessionActions } from '../../session/session.actions';
import { SegmentActions } from '../segment/segment.actions';
import { SegmentSelectors } from '../segment/segment.selectors';

import { GainsightPXService } from './gainsight-px.service';

@Injectable()
export class GainsightEffects {
  /** How often to re-attempt initialization while the native bridge is missing. */
  private static readonly INITIALIZE_RETRY_INTERVAL_MS = 1_000;

  /** How many attempts before declaring PX unavailable for the session — 30 × 1s ≈ 30s. */
  private static readonly INITIALIZE_MAX_ATTEMPTS = 30;

  /**
   * How long to give the native SDK to hand off an editor deep link before acting on it. Not a wait
   * for the bridge — the service initializes the SDK itself if it has not come up yet.
   */
  private static readonly EDITOR_HANDOFF_DELAY_MS = 1_000;

  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly gainsightPxService = inject(GainsightPXService);
  private readonly logger = inject(DatadogLogger);

  /**
   * Initialize the PX SDK as early as possible — on app ready — retrying until the native bridge
   * appears.
   *
   * The bridge (`window.gainsightpx`) is injected on page-load-complete and can land *after*
   * appReady, so a single attempt here is a race. Poll instead, and warn only if the whole window
   * elapses: a miss during the first seconds of a cold start is normal, so warning on it produced
   * noise that hid the sessions where PX genuinely never came up.
   *
   * `initialize()` is idempotent and returns whether the SDK is usable, so the happy path costs one
   * synchronous call at t=0 and the polling stops immediately.
   */
  initialize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.appReady),
        switchMap(() =>
          timer(0, GainsightEffects.INITIALIZE_RETRY_INTERVAL_MS).pipe(
            take(GainsightEffects.INITIALIZE_MAX_ATTEMPTS),
            map(() => this.gainsightPxService.initialize()),
            // Emits `true` as soon as the SDK is usable, or `false` once the attempts run out.
            first(ready => ready, false),
            filter(ready => !ready),
            tap(() =>
              this.logger.warn({
                message: '[GainsightPX] window.gainsightpx never became available',
                data: {
                  attempts: GainsightEffects.INITIALIZE_MAX_ATTEMPTS,
                  intervalMs: GainsightEffects.INITIALIZE_RETRY_INTERVAL_MS,
                },
              })
            )
          )
        )
      ),
    { dispatch: false }
  );

  /**
   * Identify the on-device user/account and set the global context once the session is fully
   * resolved. Re-runs on every app+company ready (context changes on company switch); the SDK
   * is already initialized by `initialize$`.
   */
  identify$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.appAndCompanyReady),
        concatLatestFrom(() => [
          this.store.select(SegmentSelectors.selectGainsightContext),
          this.store.select(SegmentSelectors.selectSegmentContactTraits),
          this.store.select(CompanyUserSelectors.selectCurrentCompany),
        ]),
        filter(([, ctx, contact, company]) => !!ctx && !!contact && !!company),
        tap(([, ctx, contact, company]) => {
          this.gainsightPxService.identifyUser(ctx, contact as SegmentContactTraits, company);
          this.gainsightPxService.setGlobalContext(ctx);
        })
      ),
    { dispatch: false }
  );

  /**
   * Mirror every authenticated Segment track event into the on-device PX SDK.
   *
   * In-app engagements are evaluated locally by the SDK, so only events tracked through
   * `customEvent()` on this device can trigger one — the Segment→PX cloud destination populates PX
   * analytics but never reaches the device. Identity and the session-level context come from
   * `identify$`/`setGlobalContext` (PX auto-attaches global context to every event), so the payload
   * here is just the per-event data.
   *
   * Anonymous track events are intentionally not forwarded: PX has no identified user on the
   * login/signup surfaces, so they cannot participate in engagement targeting.
   */
  trackCustomEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.track),
        tap(({ event, params }) =>
          this.gainsightPxService.customEvent(event, { ...params, ...(SEGMENT_EVENT_METADATA[event] ?? {}) })
        )
      ),
    { dispatch: false }
  );

  /**
   * Put the SDK into engagement-editing mode when the PX designer sends this device a deep link.
   *
   * Matched as a prefix, not a substring. Every deep link the OS delivers arrives on this action,
   * including the authorization callback — and that one carries a code in its query, so a URL merely
   * *containing* the PX scheme anywhere would hand a credential to the SDK.
   *
   * `enterEditingMode` catches and logs its own failures, so nothing here can throw and take the
   * effect's subscription down with it.
   */
  enterEditingMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(capacitorAppActions.appUrlOpened),
        filter(({ url }) => url.startsWith(`${environment.GAINSIGHT_PX_URL_SCHEME}://`)),
        delay(GainsightEffects.EDITOR_HANDOFF_DELAY_MS),
        tap(({ url }) => this.gainsightPxService.enterEditingMode(url))
      ),
    { dispatch: false }
  );

  resetSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.signedOut),
        tap(() => this.gainsightPxService.resetSession())
      ),
    { dispatch: false }
  );
}
